import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ieltsHeadline, scopeLabel } from '../../constants';
import type { ExamReport, IeltsBandReport, SatScoreReport } from '../../types';
import { GradingPendingStatus } from './GradingPendingStatus';
import { IeltsCriteriaBreakdown } from './IeltsCriteriaBreakdown';
import { LockedInsight, useExamInsights } from 'modules/examInsights';

interface Props {
  report: ExamReport;
  className?: string;
  /** Re-reads the report while a Writing or Speaking band is still being checked. */
  onRefresh?: () => void;
  refreshing?: boolean;
}

/** Estimated IELTS band report (TRF-style) or Digital SAT score report. */
export function ScaledReportCard({ report, className, onRefresh, refreshing }: Props) {
  return report.standard === 'IELTS' ? (
    <IeltsReport report={report} className={className} onRefresh={onRefresh} refreshing={refreshing} />
  ) : (
    <SatReport report={report} className={className} />
  );
}

const band = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : '—');

function IeltsReport({ report, className, onRefresh, refreshing }: { report: IeltsBandReport } & Omit<Props, 'report'>) {
  const { t } = useTranslation();
  const skills = [
    report.listening && { key: 'listening', label: t('exam.kind.listening'), band: report.listening.band, detail: t('exam.report.correct_of', { raw: report.listening.raw, max: report.listening.max }) },
    report.reading && { key: 'reading', label: t('exam.kind.reading'), band: report.reading.band, detail: t('exam.report.correct_of', { raw: report.reading.raw, max: report.reading.max }) },
    report.writing && { key: 'writing', label: t('exam.kind.writing'), band: report.writing.band, detail: report.writing.pending ? t('exam.report.writing_pending') : t('exam.report.writing_weights') },
    report.speaking && {
      key: 'speaking',
      label: t('exam.kind.speaking'),
      band: report.speaking.pending ? null : report.speaking.band,
      detail: report.speaking.pending ? t('exam.report.speaking_pending') : t('exam.report.speaking_answers', { count: report.speaking.recordings }),
    },
  ].filter(Boolean) as Array<{ key: string; label: string; band: number | null; detail: string }>;

  // A Listening or Reading part leads with its raw score, so the per-skill card would only repeat it.
  const lead = ieltsHeadline(report);
  const label =
    lead.kind === 'OVERALL'
      ? t('exam.report.overall')
      : lead.kind === 'SKILL'
        ? t('exam.report.skill_band', { scope: scopeLabel(t, lead.scope) })
        : t('exam.report.part_score', { scope: scopeLabel(t, lead.scope) });
  const note = lead.kind === 'OVERALL' ? t('exam.report.overall_rule') : lead.kind === 'RAW' ? t('exam.report.part_note') : null;

  return (
    <section aria-label={t('exam.report.ielts_title')} className={cn('space-y-4', className)}>
      <div className="grid grid-cols-1 gap-4 rounded-2xl bg-[#0A1628] p-6 text-slate-200 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
          {lead.kind === 'RAW' ? (
            <div className="mt-2 flex items-baseline gap-3">
              <span className="font-display text-7xl font-extrabold leading-none tracking-tight text-white">{lead.raw}/{lead.max}</span>
              <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white">{lead.percent}%</span>
            </div>
          ) : report.overall !== null ? (
            <div className="mt-2 flex items-baseline gap-3">
              <span className="font-display text-7xl font-extrabold leading-none tracking-tight text-white">{band(report.overall)}</span>
              {report.cefr && (
                <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white">CEFR {report.cefr}</span>
              )}
            </div>
          ) : (
            <GradingPendingStatus
              className="mt-2 text-white"
              tone="dark"
              label={lead.kind === 'OVERALL' ? t('exam.report.overall_pending') : t('exam.report.skill_pending')}
              onRefresh={onRefresh}
              refreshing={refreshing}
            />
          )}
        </div>
        <div className="space-y-3">
          {note && <p className="text-[13px] leading-relaxed text-slate-300">{note}</p>}
          <p className="text-[11px] leading-relaxed text-slate-500">{t('exam.report.disclaimer_ielts', { version: report.tableVersion })}</p>
        </div>
      </div>

      <div className="space-y-4">
        {lead.kind !== 'RAW' && (
          <div className={cn('grid grid-cols-1 gap-4', skills.length > 3 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3')}>
            {skills.map((s) => (
              <div key={s.key} className="rounded-2xl border border-border bg-card p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{s.label}</p>
                <p className="mt-1 font-display text-5xl font-extrabold tracking-tight text-foreground">{band(s.band)}</p>
                <p className="mt-1 text-[13px] text-muted-foreground">{s.detail}</p>
              </div>
            ))}
          </div>
        )}

        <IeltsCriteriaBreakdown report={report} />
      </div>
    </section>
  );
}

function SatReport({ report, className }: { report: SatScoreReport; className?: string }) {
  const { t } = useTranslation();
  const sections = [
    report.rw && { key: 'rw', label: t('exam.kind.sat_rw'), s: report.rw },
    report.math && { key: 'math', label: t('exam.kind.sat_math'), s: report.math },
  ].filter(Boolean) as Array<{ key: string; label: string; s: NonNullable<SatScoreReport['rw']> }>;
  const { locked } = useExamInsights();
  const bySubject = (subject: 'SAT_RW' | 'SAT_MATH') => report.domains.filter((d) => d.subject === subject);

  return (
    <section aria-label={t('exam.report.sat_title')} className={cn('space-y-4', className)}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 rounded-2xl bg-[#0F172A] p-6 text-slate-200 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{t('exam.report.total')}</p>
            <p className="mt-2 font-display text-7xl font-extrabold leading-none tracking-tight text-white">{report.total}</p>
            <p className="mt-2 text-sm text-slate-400">{t('exam.report.total_range')}</p>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500">{t('exam.report.disclaimer_sat', { version: report.tableVersion })}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {sections.map(({ key, label, s }) => (
            <div key={key} className="rounded-2xl border border-gray-200 bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{label}</p>
              <p className="mt-1 font-display text-5xl font-extrabold tracking-tight text-gray-900">{s.scaled}</p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden>
                <div className="h-full bg-gray-900" style={{ width: `${((s.scaled - 200) / 600) * 100}%` }} />
              </div>
              <p className="mt-2 text-[13px] text-gray-500">{t('exam.report.correct_of', { raw: s.raw, max: s.max })}</p>
            </div>
          ))}
        </div>
      </div>

      {report.domains.length === 0 && locked && (
        <LockedInsight title={t('exam_insights.domains_title')} description={t('exam_insights.domains_body')} lines={5} />
      )}

      {report.domains.length > 0 && (
        <div className="grid grid-cols-1 gap-4 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 md:grid-cols-2">
          {(['SAT_RW', 'SAT_MATH'] as const).map((subject) =>
            bySubject(subject).length ? (
              <div key={subject}>
                <p className="mb-3 font-display font-bold text-gray-900">{t(subject === 'SAT_RW' ? 'exam.kind.sat_rw' : 'exam.kind.sat_math')}</p>
                <ul className="space-y-3">
                  {bySubject(subject).map((d) => (
                    <li key={d.domain}>
                      <div className="flex items-center justify-between text-[13px] text-gray-700">
                        <span>{d.domain === 'Other' ? t('exam.report.other_domain') : d.domain}</span>
                        <span className="font-mono">{d.correct}/{d.total}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full bg-primary-600" style={{ width: `${d.total ? (d.correct / d.total) * 100 : 0}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null,
          )}
        </div>
      )}
    </section>
  );
}

export default ScaledReportCard;
